import { NotificationT, Response } from 'lib-shared-legacy-types';
import { media } from 'lib-shared-types-channels';
import find from 'lodash/find';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { Dictionary } from 'ts-essentials';

import {
  customFieldsApi,
  ReadAttributesApiResponse,
  ReadValueApiResponse,
} from 'apis/customFields';
import { PresenceAgent } from 'apis/presence/types/presence.types';
import { appActions } from 'app/app.actions';
import { busylightActions } from 'app/views/layout/views/settings/views/preferences/views/preferencesConnectedDevices/views/busylight/busylight.actions';
import {
  acwNotificationType,
  getAcwNotification,
  getAcwNotificationIdByCsid,
} from 'components/conversation/utils/acwNotification';
import { selectCurrentNotificationsByType } from 'components/notifications/selectors/notifications';
import { mediaToChannels } from 'utils/channels';
import { getCustomFields, isCustomFieldValueEmpty } from 'utils/customFields';
import { evalCustomFieldsVisibility } from 'utils/customFields/useCustomFieldsVisibility';
import { getMediaFeatureFlagStatus } from 'utils/featureFlagging/channelFeatureFlags';
import { featureFlags } from 'utils/featureFlagging/featureFlags';

import { actions as conversationActions } from '../../conversation/actions/conversation';
import { notificationActions } from '../../notifications/actions/notification';
import { presenceActions } from '../actions/presenceActions';
import * as presenceAPI from '../api/presence';
import { PresenceV2Data, PresenceWorkload } from '../api/types';

const { notificationAdd, notificationRemoveById, notificationFaviconChange } =
  notificationActions;
const { conversationCompleteAcw } = conversationActions;
const {
  presenceGetAll,
  presenceGetAllSucceeded,
  presenceGetAllFailed,

  presenceSetStatus,
  presenceSetStatusSucceeded,
  presenceSetStatusFailed,

  presenceSetMediaChannel,
  presenceSetMediaChannelSucceeded,
  presenceSetMediaChannelFailed,

  presenceSetMedia,
  presenceSetMediaSucceeded,
  presenceSetMediaFailed,

  presenceSetSimultaneousRinging,
  presenceSetSimultaneousRingingSucceeded,
  presenceSetSimultaneousRingingFailed,

  presenceGet,
  presenceGetSucceeded,
  presenceGetFailed,

  presenceWsSetOwnStatus,

  presenceSetOthersSimultaneousRinging,
  presenceSetOthersSimultaneousRingingSucceeded,
  presenceSetOthersSimultaneousRingingFailed,
} = presenceActions;

// Saga
export const presence = [
  takeLatest(
    presenceSetOthersSimultaneousRinging.toString() as any,
    genWithPromise(
      presenceAPI.changeSimultaneousRinging,
      presenceSetOthersSimultaneousRingingSucceeded,
      presenceSetOthersSimultaneousRingingFailed,
    ),
  ),

  takeLatest(
    presenceGetAll.toString() as any,
    genGetAllPresenceWithPromise(
      presenceAPI.presenceGetall,
      presenceGetAllSucceeded,
      presenceGetAllFailed,
    ),
  ),

  takeLatest(
    presenceGet.toString() as any,
    genGetPresenceWithPromise(
      getPresence,
      presenceGetSucceeded,
      presenceGetFailed,
    ),
  ),

  takeEvery(
    presenceSetStatus.toString() as any,
    genSetPresenceWithPromise(
      presenceAPI.changeStatus,
      presenceSetStatusSucceeded,
      presenceSetStatusFailed,
    ),
  ),

  takeEvery(
    presenceSetMediaChannel.toString() as any,
    genWithPromise(
      presenceAPI.changeMediaChannel,
      presenceSetMediaChannelSucceeded,
      presenceSetMediaChannelFailed,
    ),
  ),

  takeEvery(
    presenceSetMedia.toString() as any,
    genWithPromise(
      presenceAPI.changeMedia,
      presenceSetMediaSucceeded,
      presenceSetMediaFailed,
    ),
  ),

  takeEvery(
    presenceSetSimultaneousRinging.toString() as any,
    genWithPromise(
      presenceAPI.changeSimultaneousRing,
      presenceSetSimultaneousRingingSucceeded,
      presenceSetSimultaneousRingingFailed,
    ),
  ),

  takeEvery(presenceWsSetOwnStatus.toString(), genOwnPresence()),
];
const { acwLogicOnBackend } = featureFlags.get(['acw-logic-on-backend']);

function genOwnPresence() {
  return function* (action: any) {
    if (
      action.payload &&
      action.payload.params &&
      action.payload.params.presence
    ) {
      if (action.payload.params && action.payload.params.new_status) {
        yield put(
          notificationFaviconChange({ type: action.payload.params.new_status }),
        ); // We update the favicon.

        yield put(
          busylightActions.setStatus({
            status: action.payload.params.new_status,
          }),
        );
      }

      if (
        action.payload.params.presence.load_descriptions &&
        Array.isArray(action.payload.params.presence.load_descriptions)
      ) {
        // Remove unnecessary ACW notification
        const inAcwCsids: string[] =
          action.payload.params.presence.load_descriptions
            .filter(({ load_type }) => load_type === 'acw')
            .map(({ csid }) => csid);
        const notifications: NotificationT[] = yield select((state) =>
          selectCurrentNotificationsByType(state, acwNotificationType),
        );

        for (let i = 0; i < notifications.length; i++) {
          const id = notifications[i].id;
          if (
            inAcwCsids.every((csid) => getAcwNotificationIdByCsid(csid) !== id)
          ) {
            yield put(notificationRemoveById({ id }));
          }
        }
      }
    }
  };
}

async function getPresence(payload: {
  user_id: string;
}): Promise<Response<Dictionary<unknown> & PresenceV2Data, null>> {
  const [responseV1, responseV2] = await Promise.all([
    presenceAPI.presenceGet(payload),
    presenceAPI.presenceGetV2(payload),
  ]);

  return {
    data: { ...(responseV1 as any).data, ...responseV2.data },
    meta: null,
  };
}

function genGetPresenceWithPromise(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: { user_id: string };
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result: any = {};

    try {
      yield put(appActions.getPresence.pending());
      result = yield call(apiAction, action.payload);

      if (result) {
        result.user_id = action.payload ? action.payload : null;
      }
      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
      yield put(appActions.getPresence.fulfilled());

      // Update the favicon.
      const agentPresence = result.data;

      if (agentPresence) {
        // We check if there if any ACW on.
        if (
          agentPresence.presence.presence_status &&
          agentPresence.presence.presence_status.load_descriptions &&
          Array.isArray(
            agentPresence.presence.presence_status.load_descriptions,
          )
        ) {
          yield put(
            notificationFaviconChange({
              type: agentPresence.presence.status,
            }),
          ); // We update the favicon.

          yield put(
            busylightActions.setStatus({
              status: agentPresence.presence.status,
            }),
          );

          for (const workload of result.data.workloads as PresenceWorkload[]) {
            if (workload.type === 'acw') {
              if (workload.expires_at === null) {
                // if there is infinite wrap-up, we have to check if it's still valid.
                // If not, we request to end the wrap-up time.
                // TODO [CONV-230]: remove this logic
                const requests = yield all([
                  put(
                    customFieldsApi.endpoints.readAttributes.initiate({
                      entityId: 'conversation',
                    }) as never,
                  ),
                  put(
                    customFieldsApi.endpoints.readValue.initiate({
                      entityId: 'conversation',
                      entityInstanceId: `${workload.csid}`,
                    }) as never,
                  ),
                ]);
                const responses = yield all(requests);
                const attributesResponse: ReadAttributesApiResponse =
                  responses[0].data;
                const valueResponse: ReadValueApiResponse = responses[1].data;

                const customFields = getCustomFields(
                  attributesResponse.data.attributes,
                  valueResponse.data.attribute_values,
                );

                const metaConv = yield select((state) =>
                  state.conversations.metaConversations.find(
                    (mC) => workload.csid === mC.conversation.id,
                  ),
                );
                const visibility =
                  customFields.length > 0
                    ? evalCustomFieldsVisibility(customFields, {
                        queue: [metaConv?.conversation.queue?.id ?? ''],
                      })
                    : null;
                if (
                  visibility !== null &&
                  customFields.some(
                    ({ attribute: { id, is_required }, value }) =>
                      visibility[id] &&
                      is_required &&
                      isCustomFieldValueEmpty(value),
                  )
                ) {
                  // If the infinite wrap-up is valid, show the notification
                  yield put(
                    notificationAdd(
                      getAcwNotification({
                        csid: workload.csid,
                        expiration: { time: workload.expires_at },
                      }),
                    ),
                  );
                } else if (!acwLogicOnBackend) {
                  // If the infinite wrap-up is not valid, end it.
                  yield put(
                    conversationCompleteAcw({
                      csid: workload.csid,
                    }),
                  );
                }
              } else {
                // if there is finite wrap-up, just show it
                yield put(
                  notificationAdd(
                    getAcwNotification({
                      csid: workload.csid,
                      expiration: { time: workload.expires_at },
                    }),
                  ),
                );
              }
            }
          }
        }
      }
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
      yield put(appActions.getPresence.pending());
    }
  };
}

function genGetAllPresenceWithPromise(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result: any = {};

    try {
      yield put(appActions.getPresence.pending());
      if (!action.payload) {
        const getSettings = (state) => state.settings;
        const settingsState = yield select(getSettings);

        if (settingsState.profile.profile.id !== '') {
          throw 'PresenceGetall needs a user id in payload params';
        } else {
          result = yield call(apiAction, settingsState.profile.profile.id);
        }
      } else {
        result = yield call(apiAction, action.payload);
      }

      if (result) {
        result.user_id = action.payload ? action.payload : null;
      }
      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
      yield put(appActions.getPresence.fulfilled());

      // Update the favicon.
      if (result && result.data && action.payload) {
        const agentPresence = find(
          result.data,
          (userPresence: any) => userPresence.user.id === action.payload,
        ) as PresenceAgent;
        if (agentPresence) {
          const { presence } = agentPresence;
          const {
            working_channels: workingChannels,
            presence_status: presenceStatus,
          } = presence;
          const { load_descriptions: loadDescriptions } = presenceStatus;

          const mediaFeatureFlagStatus = getMediaFeatureFlagStatus();

          for (const mediaName of media) {
            const mediaChannels = mediaToChannels[mediaName];

            const workingStateForMedia = !!mediaChannels.find((channel) => {
              return (
                workingChannels.find(
                  (workingChannel) => workingChannel.channel === channel,
                )?.working ?? false
              );
            });

            if (
              workingStateForMedia &&
              !mediaFeatureFlagStatus.get(mediaName)
            ) {
              // Change media presence to disabled
              console.log(
                `${mediaName} was on, but user is not allowed on channel, switching to off`,
              );
              yield put(presenceSetMedia({ media: mediaName, working: false }));
            }
          }

          // We check if there if any ACW on.
          if (
            presenceStatus &&
            loadDescriptions &&
            Array.isArray(loadDescriptions)
          ) {
            yield put(
              notificationFaviconChange({
                type: agentPresence.presence.status,
              }),
            ); // We update the favicon.

            yield put(
              busylightActions.setStatus({
                status: agentPresence.presence.status,
              }),
            );
          }
        }
      }
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
      yield put(appActions.getPresence.rejected());
    }
  };
}

function genSetPresenceWithPromise(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result;

    try {
      result = yield call(apiAction, action.payload);
      yield call(action.meta.resolve as any, result);
      if (result) {
        const userId = yield select(
          (state) => state.settings.profile.profile.id,
        );
        result.params = { user_id: userId };
      }

      yield put(resultSucceededAction(result));

      // Update the favicon.
      if (result && result.data && result.data.presence_status_category) {
        yield put(
          notificationFaviconChange({
            type: result.data.presence_status_category,
          }),
        ); // We update the favicon.

        yield put(
          busylightActions.setStatus({
            status: result.data.presence_status_category,
          }),
        );
      }
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
    }
  };
}

function genWithPromise(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result;

    try {
      result = yield call(apiAction, action.payload);
      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
    }
  };
}
